import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiService } from 'src/app/services/api.service';

@Component({
  selector: 'merchant-upload-modal',
  templateUrl: './merchant-upload.component.html',
  styleUrls: ['./merchant-upload.component.scss'],
})
export class MerchantUploadDialog {
  selectedFile: File | null = null;
  uploading = false;
  isDraggingOver = false;
  uploadResult: {
    totalRecords: number;
    savedCount: number;
    duplicateCount: number;
    invalidCount: number;
    duplicateTransferIds: string[];
  } | null = null;
  uploadStatus: 'success' | 'partial' | 'failure' | null = null;

  constructor(
    public dialogRef: MatDialogRef<MerchantUploadDialog>,
    @Inject(MAT_DIALOG_DATA) public data: { employeeId: number; cycleStart: string; cycleEnd: string },
    private apiService: ApiService,
    private snackBar: MatSnackBar
  ) {}

  onNoClick(): void {
    this.dialogRef.close();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFile(files[0]);
    }
  }

  onFileSelected(event: any): void {
    const file: File = event.target.files[0];
    if (file) {
      this.handleFile(file);
    }
  }

  handleFile(file: File): void {
    const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'csv'];
    if (validTypes.includes(file.type) || file.name.endsWith('.csv') || file.name.endsWith('.xls') || file.name.endsWith('.xlsx')) {
      this.selectedFile = file;
    } else {
      this.snackBar.open('Please select a valid CSV or Excel file', 'Close', { duration: 3000 });
      this.selectedFile = null;
    }
  }

  onUpload(): void {
    if (!this.selectedFile) {
      this.snackBar.open('Please select a file to upload', 'Close', { duration: 3000 });
      return;
    }

    this.uploading = true;
    const formData = new FormData();
    formData.append('file', this.selectedFile);
    formData.append('empId', this.data.employeeId.toString());
    formData.append('billingCycleStart', this.data.cycleStart);
    formData.append('billingCycleEnd', this.data.cycleEnd);

    const loggedInUser = window.localStorage.getItem('loggedUserName') || 'system';
    formData.append('createdBy', loggedInUser);

    this.apiService.uploadMerchantData(formData).subscribe({
      next: (response) => {
        this.uploading = false;
        this.uploadResult = response;

        const total = response?.totalRecords ?? 0;
        const saved = response?.savedCount ?? 0;
        const skipped = (response?.duplicateCount ?? 0) + (response?.invalidCount ?? 0);

        if (saved > 0 && skipped === 0) {
          this.uploadStatus = 'success';
          this.snackBar.open(`Full success: all ${saved} record(s) uploaded`, 'Close', {
            duration: 4000,
            panelClass: ['snackbar-success']
          });
        } else if (saved > 0) {
          this.uploadStatus = 'partial';
          this.snackBar.open(`Partial success: ${saved} uploaded, ${skipped} skipped of ${total}`, 'Close', {
            duration: 5000,
            panelClass: ['snackbar-warning']
          });
        } else {
          this.uploadStatus = 'failure';
          this.snackBar.open(`Full failure: no records uploaded (${skipped} skipped of ${total})`, 'Close', {
            duration: 5000,
            panelClass: ['snackbar-error']
          });
        }
      },
      error: (error) => {
        this.uploading = false;
        this.uploadStatus = 'failure';
        this.snackBar.open('Error uploading file: ' + (error.message || 'Unknown error'), 'Close', {
          duration: 5000,
          panelClass: ['snackbar-error']
        });
      }
    });
  }

  done(): void {
    // Close with true if anything was saved so the list refreshes
    this.dialogRef.close((this.uploadResult?.savedCount ?? 0) > 0);
  }

  uploadAnother(): void {
    this.selectedFile = null;
    this.uploadResult = null;
    this.uploadStatus = null;
  }

  removeFile(): void {
    this.selectedFile = null;
  }
}
